/**
 * Toast - Simple toast notification system to replace window.alert
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

type ToastType = "info" | "success" | "error" | "warning";

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let toastId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType = "info") => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    // Errors stay until explicitly dismissed — they must not vanish unread.
    if (type !== "error") setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  const contextValue: ToastContextType = {
    toast: addToast,
    success: (msg) => addToast(msg, "success"),
    error: (msg) => addToast(msg, "error"),
    warning: (msg) => addToast(msg, "warning"),
    info: (msg) => addToast(msg, "info"),
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;

  const containerStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 20,
    right: 20,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    zIndex: 9998,
    maxWidth: 360,
  };

  return (
    <div style={containerStyle}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastMessage;
  onDismiss: () => void;
}) {
  const [isExiting, setIsExiting] = useState(false);
  const persistent = toast.type === "error";

  useEffect(() => {
    if (persistent) return;
    const timer = setTimeout(() => setIsExiting(true), 3500);
    return () => clearTimeout(timer);
  }, [persistent]);

  const bgColors: Record<ToastType, string> = {
    info: "var(--color-brand)",
    success: "var(--color-success)",
    error: "var(--color-danger)",
    warning: "var(--color-warning)",
  };

  const style: React.CSSProperties = {
    background: bgColors[toast.type],
    color: "#fff",
    padding: "12px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    boxShadow: "var(--shadow-md)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    opacity: isExiting ? 0 : 1,
    transform: isExiting ? "translateX(20px)" : "translateX(0)",
    transition: "opacity 0.3s, transform 0.3s",
    cursor: "pointer",
  };

  return (
    <div
      style={style}
      onClick={onDismiss}
      role={toast.type === "error" || toast.type === "warning" ? "alert" : "status"}
      title="Dismiss"
    >
      <span>{toast.message}</span>
      <span aria-hidden style={{ opacity: 0.7, fontSize: 16 }}>×</span>
    </div>
  );
}
